import React from 'react';

// Global Error Tracker Class
class GlobalErrorTracker {
    constructor(config = {}) {
        this.config = {
            apiEndpoint: config.apiEndpoint || '/api/report-error',
            token: localStorage.getItem('token'),
            enableConsoleLog: config.enableConsoleLog !== false,
            enableEmailAlert: config.enableEmailAlert !== false,
            userId: config.userId || 'anonymous',
            appVersion: config.appVersion || '1.0.0',
            ...config
        };

        this.errorQueue = [];
        this.isOnline = navigator.onLine;
        this.setupErrorHandlers();
    }

    setupErrorHandlers() {
        // 1. JavaScript Runtime Errors (syntax errors, reference errors, etc.)
        window.addEventListener('error', (event) => {
            this.handleError({
                type: 'JAVASCRIPT_ERROR',
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error.stack,
                severity: 'high'
            });
        });

        // 2. Unhandled Promise Rejections (async/await errors, API failures)
        window.addEventListener('unhandledrejection', (event) => {
            this.handleError({
                type: 'UNHANDLED_PROMISE_REJECTION',
                message: event.reason.message || 'Unhandled Promise Rejection',
                stack: event.reason.stack,
                reason: event.reason,
                severity: 'high'
            });
        });

        // 3. Network/Connection Errors
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.flushErrorQueue();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            this.handleError({
                type: 'NETWORK_ERROR',
                message: 'Network connection lost',
                severity: 'medium'
            });
        });

        // 4. Resource Loading Errors (images, scripts, stylesheets)
        window.addEventListener('error', (event) => {
            if (event.target !== window) {
                this.handleError({
                    type: 'RESOURCE_LOADING_ERROR',
                    message: `Failed to load resource: ${event.target.src || event.target.href}`,
                    element: event.target.tagName,
                    source: event.target.src || event.target.href,
                    severity: 'medium'
                });
            }
        }, true);
    }

    // Intercept Axios Errors
    setupAxiosInterceptor(axiosInstance) {
        axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                const url = error.config.url || '';
                if (url.includes(this.config.apiEndpoint)) {
                    return Promise.reject(error); // Skip self-reporting errors
                }

                this.handleError({
                    type: 'AXIOS_ERROR',
                    message: error.message,
                    status: error.response.status,
                    statusText: error.response.statusText,
                    url,
                    method: error.config.method.toUpperCase(),
                    data: error.config.data,
                    responseData: error.response.data,
                    severity: 'high'
                });

                return Promise.reject(error);
            }
        );
    }

    // Intercept Fetch API Errors
    setupFetchInterceptor() {
        const originalFetch = window.fetch;
        const self = this;

        const ignoredUrls = [
            'chrome-extension://',
            'safari-extension://',
            'moz-extension://',
            'edge-extension://',
            'https://www.google-analytics.com'
        ];

        window.fetch = async function (...args) {
            const url = typeof args[0] === 'string' ? args[0] : args[0].url;
            // Skip error reporting endpoint and empty URLs
            if (!url || url.includes(self.config.apiEndpoint)) {
                return originalFetch(...args);
            }
            if (ignoredUrls.some(prefix => url.startsWith(prefix))) {
                return originalFetch(...args);
            }

            try {
                const response = await originalFetch(...args);
                // Only report real HTTP errors (status >= 400)
                // Skip status 0 which might be aborted requests
                if (!response.ok && response.status > 0) {
                    self.handleError({
                        type: 'FETCH_API_ERROR',
                        message: `HTTP ${response.status}: ${response.statusText}`,
                        status: response.status,
                        statusText: response.statusText,
                        url,
                        method: args[1].method || 'GET',
                        severity: 'high'
                    });
                }

                return response;
            } catch (error) {
                // Only report network errors that are actual errors
                if (error.name !== 'AbortError') {
                    self.handleError({
                        type: 'FETCH_NETWORK_ERROR',
                        message: error.message,
                        url,
                        method: args[1].method || 'GET',
                        stack: error.stack,
                        severity: 'high'
                    });
                }
                throw error;
            }
        };
    }

    // Main Error Handler
    handleError(errorData) {
        // Skip status 0 errors which are often false positives
        if (errorData.status === 0) return;

        const enrichedError = {
            ...errorData,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            userId: this.config.userId,
            appVersion: this.config.appVersion,
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight
            },
            sessionId: this.getSessionId()
        };

        if (this.config.enableConsoleLog) {
            console.error('🚨 Global Error Captured:', enrichedError);
        }

        this.errorQueue.push(enrichedError);
        this.processErrorQueue();
    }

    // Process Error Queue
    async processErrorQueue() {
        if (!this.isOnline || this.errorQueue.length === 0 || !this.config.enableEmailAlert) return;

        const errorsToSend = [...this.errorQueue];
        this.errorQueue = [];

        try {
            await this.sendErrorsToServer(errorsToSend);
        } catch (error) {
            console.error('Failed to send errors to server:', error);
            // Put errors back in queue if sending failed
            this.errorQueue.unshift(...errorsToSend);
        }
    }

    // Send Errors to Server
    async sendErrorsToServer(errors) {
        if (!this.config.enableEmailAlert) return;
        if (!this.config.enableEmailAlert || !errors || errors.length === 0) return;

        const response = await fetch(this.config.apiEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.config.token}`,
            },
            body: JSON.stringify({
                errors,
                metadata: {
                    timestamp: new Date().toISOString(),
                    batchSize: errors.length
                }
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
    }

    // Flush Error Queue (when network comes back online)
    flushErrorQueue() {
        if (this.errorQueue.length > 0) {
            this.processErrorQueue();
        }
    }

    // Get or Generate Session ID
    getSessionId() {
        let sessionId = sessionStorage.getItem('error-tracker-session');
        if (!sessionId) {
            sessionId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
            sessionStorage.setItem('error-tracker-session', sessionId);
        }
        return sessionId;
    }

    // Manual Error Reporting
    reportError(error, context = {}) {
        this.handleError({
            type: 'MANUAL_ERROR',
            message: error.message,
            stack: error.stack,
            context,
            severity: 'medium'
        });
    }
}

// React Error Boundary for Component Errors
class GlobalErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        if (window.globalErrorTracker) {
            window.globalErrorTracker.handleError({
                type: 'REACT_COMPONENT_ERROR',
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                severity: 'high'
            });
        }
    }

    render() {
        if (this.state.hasError) {
            console.error('Component Error:', this.props.hasError);
            return (
                <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
                        <p className="text-gray-600 mb-6">
                            We've encountered an error. Our team has been notified.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Initialize Global Error Tracker
const initializeErrorTracker = () => {
    console.log('Initializing Global Error Tracker');
    window.globalErrorTracker = new GlobalErrorTracker({
        apiEndpoint: '/api/report-error',
        enableConsoleLog: true,
        enableEmailAlert: true,
        userId: 'demo-user-123',
        appVersion: '1.0.0'
    });

    // Setup fetch interceptor
    window.globalErrorTracker.setupFetchInterceptor();
};

export { GlobalErrorBoundary, initializeErrorTracker };